import { Dispatch, SetStateAction, useState } from 'react';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import {
  bulkActivateWalkthroughsFromAPI,
  fetchSuiteWalkThroughs,
} from 'src/services/client';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { BulkActivateWalkthroughsPayload } from 'src/components/BulkOperations/Walkthroughs/BulkActivateWalkthroughs/Custom-Hooks/types';
import { IWalkthroughModel } from 'src/services/client-types';
import { toast } from 'react-toastify';

export interface UseBulkActivateWalkthroughsReturn {
  selectedUnitIds: Set<string>;
  walkthroughsByUnit: Map<string, IWalkthroughModel[]>;
  isLoadingUnit: string | null;
  handleUnitToggle: (
    unitId: string,
    unit: UnitBulkOperationDto,
    isChecked: boolean
  ) => Promise<void>;
  getWalkthroughsForUnit: (unitId: string) => IWalkthroughModel[];
  activateWalkthroughs: boolean;
  setActivateWalkthroughs: React.Dispatch<React.SetStateAction<boolean>>;
  sendSchedulingEmail: boolean;
  setSendSchedulingEmail: (value: boolean) => void;
  selectedWalkthroughIds: Set<string>;
  handleWalkthroughToggle: (walkthroughId: string, isChecked: boolean) => void;
  handleSelectAllForUnit: (walkthroughs: IWalkthroughModel[]) => void;
  handleDeselectAllForUnit: (walkthroughs: IWalkthroughModel[]) => void;
  onSelectAllWalkthroughs: () => void;
  onDeselectAllWalkthroughs: () => void;
  isFetchingWalkthroughs: boolean;
  performBulkActivation: any;
  bulkActivateResponseData: IWalkthroughModel[];
  isActivationSuccess: boolean;
  resetActivationState: () => void;
  isSubmitting: boolean;
  confirmActivation: () => Promise<void>;
  setShowConfirmation: Dispatch<SetStateAction<boolean>>;
  showConfirmation: boolean;
  error: string | null;
  clearError: () => void;
}

export const useBulkActivateWalkthroughs = (
  simpleSuitesForWalkthroughDialogue: UnitBulkOperationDto[],
  bookingLinkId: string | null
) => {
  const [selectedUnitIdSet, setSelectedUnitIdSet] = useState<Set<string>>(
    new Set()
  );
  const [unitWalkthroughMap, setUnitWalkthroughMap] = useState<
    Map<string, IWalkthroughModel[]>
  >(new Map());

  const [isLoadingUnit, setIsLoadingUnit] = useState<string | null>(null);
  const [isFetchingWalkthroughs, setIsFetchingWalkthroughs] =
    useState<boolean>(false);
  const [selectedWalkthroughIds, setSelectedWalkthroughIds] = useState<
    Set<string>
  >(new Set());

  const [activateWalkthroughs, setActivateWalkthroughs] = useState(false);
  const [sendSchedulingEmail, setSendSchedulingEmail] = useState(false);

  const [bulkActivateResponseData, setBulkActivateResponseData] = useState<
    IWalkthroughModel[] | null
  >(null);
  const [isActivationSuccess, setIsActivationSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showConfirmation, setShowConfirmation] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const user: any = useSelector(selectAuthenticatedUser);
  const userRole: any = user?.roles?.[0]?.role?.name;

  const clearIneligibleUnitSelections = () => {
    const ineligibleUnitIds: Set<string> = new Set();

    const filteredSelectedUnitIds: Set<string> = new Set([
      ...selectedUnitIdSet,
    ]);

    const filteredSelectedWalkthroughIds: Set<string> = new Set([
      ...selectedWalkthroughIds,
    ]);

    if (
      !simpleSuitesForWalkthroughDialogue ||
      simpleSuitesForWalkthroughDialogue.length === 0
    ) {
      console.error(
        'purgeIneligibleSelections called but simpleSuitesForWalkthroughDialogue is empty/null'
      );
      return;
    }

    for (const walkthrough of simpleSuitesForWalkthroughDialogue) {
      if (!walkthrough.hasPrimaryContact) {
        ineligibleUnitIds.add(walkthrough.id);
      }
    }

    for (const unitId of selectedUnitIdSet) {
      if (ineligibleUnitIds.has(unitId)) {
        filteredSelectedUnitIds.delete(unitId);
      }
    }

    const deepCopiedWalkthroughsByUnit: Map<string, IWalkthroughModel[]> =
      structuredClone(unitWalkthroughMap);

    for (const [unitId, walkthroughs] of deepCopiedWalkthroughsByUnit) {
      if (ineligibleUnitIds.has(unitId)) {
        walkthroughs.forEach((w: IWalkthroughModel) => {
          filteredSelectedWalkthroughIds.delete(w.id);
        });

        deepCopiedWalkthroughsByUnit.delete(unitId);
      }
    }

    setSelectedUnitIdSet(filteredSelectedUnitIds);
    setSelectedWalkthroughIds(filteredSelectedWalkthroughIds);
    setUnitWalkthroughMap(deepCopiedWalkthroughsByUnit);
  };

  const updateSchedulingEmail = (newValue: boolean) => {
    setSendSchedulingEmail(newValue);

    if (newValue) {
      clearIneligibleUnitSelections();
    } else {
      clearNonActivateStatusSelections();
    }
  };

  const onSelectAllWalkthroughs = () => {
    const setForWalkthroughsIds = new Set<string>();

    for (const walkthroughsArray of unitWalkthroughMap.values()) {
      for (const walkthrough of walkthroughsArray) {
        if (walkthrough.status === 'Schedule') {
          continue;
        }

        if (!sendSchedulingEmail && walkthrough.status !== 'Activate') {
          continue;
        }

        setForWalkthroughsIds.add(walkthrough.id);
      }
    }

    setSelectedWalkthroughIds(setForWalkthroughsIds);
  };

  const onDeselectAllWalkthroughs = () => {
    setSelectedWalkthroughIds(new Set());
  };

  const handleWalkthroughToggle = (
    walkthroughId: string,
    isChecked: boolean
  ) => {
    const updatedWalkthroughIds = new Set(selectedWalkthroughIds);

    if (isChecked) {
      updatedWalkthroughIds.add(walkthroughId);
    } else {
      updatedWalkthroughIds.delete(walkthroughId);
    }

    setSelectedWalkthroughIds(updatedWalkthroughIds);
  };

  const handleSelectAllForUnit = (walkthroughs: IWalkthroughModel[]) => {
    const updatedWalkthroughIds: Set<string> = new Set(selectedWalkthroughIds);

    walkthroughs.forEach((walkthrough: IWalkthroughModel) => {
      if (walkthrough.status === 'Schedule') {
        return;
      }

      if (!sendSchedulingEmail && walkthrough.status !== 'Activate') {
        return;
      }

      updatedWalkthroughIds.add(walkthrough.id);
    });

    setSelectedWalkthroughIds(updatedWalkthroughIds);
  };

  const handleDeselectAllForUnit = (walkthroughs: IWalkthroughModel[]) => {
    const updatedWalkthroughIds: Set<string> = new Set(selectedWalkthroughIds);

    walkthroughs.forEach((walkthrough: any) => {
      updatedWalkthroughIds.delete(walkthrough.id);
    });

    setSelectedWalkthroughIds(updatedWalkthroughIds);
  };

  const addUnitToSelection = (unitId: string) => {
    const updatedUnitIds: Set<string> = new Set(selectedUnitIdSet);
    updatedUnitIds.add(unitId);
    setSelectedUnitIdSet(updatedUnitIds);
  };

  const clearNonActivateStatusSelections = () => {
    const filteredSelectedWalkthroughIds = new Set<string>();

    for (const [unitId, walkthroughs] of unitWalkthroughMap) {
      for (const walkthrough of walkthroughs) {
        if (
          walkthrough.status === 'Activate' &&
          selectedWalkthroughIds.has(walkthrough.id)
        ) {
          filteredSelectedWalkthroughIds.add(walkthrough.id);
        }
      }
    }

    setSelectedWalkthroughIds(filteredSelectedWalkthroughIds);
  };

  const removeUnitFromSelection = (unitId: string) => {
    const updatedUnitIds: Set<string> = new Set(selectedUnitIdSet);
    const walkthroughsForUnit: IWalkthroughModel[] =
      unitWalkthroughMap.get(unitId);

    if (walkthroughsForUnit) {
      handleDeselectAllForUnit(walkthroughsForUnit);
      removeWalkthroughsForUnit(unitId);
    }

    updatedUnitIds.delete(unitId);

    if (updatedUnitIds.size === 0) {
      setActivateWalkthroughs(false);
      setSendSchedulingEmail(false);
    }

    setSelectedUnitIdSet(updatedUnitIds);
  };

  const addWalkthroughsForUnit = (unitId: string, walkthroughs: any) => {
    const updatedWalkthroughs = new Map(unitWalkthroughMap);
    updatedWalkthroughs.set(unitId, walkthroughs);
    setUnitWalkthroughMap(updatedWalkthroughs);
  };

  const removeWalkthroughsForUnit = (unitId: string) => {
    const updatedWalkthroughs = new Map(unitWalkthroughMap);
    updatedWalkthroughs.delete(unitId);
    setUnitWalkthroughMap(updatedWalkthroughs);
  };

  const handleUnitToggle = async (
    unitId: string,
    unit: UnitBulkOperationDto,
    isChecked: boolean
  ) => {
    if (!isChecked) {
      removeUnitFromSelection(unitId);
      removeWalkthroughsForUnit(unitId);
      return;
    }

    if (!userRole) {
      console.error('No user role found');
      alert('Unable to load walkthroughs: User role not found');
      return;
    }

    setIsLoadingUnit(unitId);
    setIsFetchingWalkthroughs(true);

    try {
      const response: any = await fetchSuiteWalkThroughs(unitId, userRole);
      const responseJson = await response.json();
      const walkthroughsFromApi: IWalkthroughModel[] = responseJson.data || [];

      const filteredWalkthroughsNoActivateStatus: IWalkthroughModel[] =
        walkthroughsFromApi.filter(
          (walkthrough: IWalkthroughModel) =>
            walkthrough.status !== 'Schedule' &&
            walkthrough.status !== 'Awaiting Scheduling' &&
            walkthrough.status !== 'Review' &&
            walkthrough.status !== 'Configure' &&
            walkthrough.status !== 'Begin' &&
            (
              walkthrough.walkthroughType === 'Home Owner Walk-through' ||
              ((walkthrough.walkthroughType === 'Enhanced' || walkthrough.walkthroughType === 'Simplified') && walkthrough.permissions === 'Homeowner or Occupant')
            )
        );

      addUnitToSelection(unitId);
      addWalkthroughsForUnit(unitId, filteredWalkthroughsNoActivateStatus);
    } catch (error) {
      console.error('Failed to load walkthroughs:', error);
      toast.error(
        `Failed to load walkthroughs for unit ${unit.number}. Please try again.`
      );
    } finally {
      setIsLoadingUnit(null);
      setIsFetchingWalkthroughs(false);
    }
  };

  const performBulkActivation = () => {
    setShowConfirmation(true);
  };

  const confirmActivation = async () => {
    const selectedWalkthroughIdsArray: string[] = [...selectedWalkthroughIds];

    const requestPayload: BulkActivateWalkthroughsPayload = {
      selectedWalkthroughIDs: selectedWalkthroughIdsArray,
      sendSchedulingEmail,
      activateWalkthroughs,
      propertyBookingLinkId: bookingLinkId,
    };

    setError(null);
    setIsSubmitting(true);

    try {
      const bulkActivateWalkthroughResData: IWalkthroughModel[] =
        await bulkActivateWalkthroughsFromAPI(requestPayload);

      setBulkActivateResponseData(bulkActivateWalkthroughResData);
      setIsActivationSuccess(true);

      toast.success('Walkthroughs activated successfully!', {
        autoClose: 5000,
        position: 'top-center',
      });
    } catch (activationError) {
      console.error('Failed to activate walkthroughs:', activationError);
      setError(
        'There was an issue with bulk activating that walkthrough. Please contact support.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const getWalkthroughsForUnit = (unitId: string): any => {
    return unitWalkthroughMap.get(unitId) || [];
  };

  const resetActivationState = () => {
    setBulkActivateResponseData(null);
    setIsActivationSuccess(false);
    setSelectedUnitIdSet(new Set());
    setUnitWalkthroughMap(new Map());
    setSelectedWalkthroughIds(new Set());
    setActivateWalkthroughs(false);
    setSendSchedulingEmail(false);
    setShowConfirmation(false);
    setError(null);
  };

  return {
    selectedUnitIds: selectedUnitIdSet,
    walkthroughsByUnit: unitWalkthroughMap,
    isLoadingUnit,
    handleUnitToggle,
    getWalkthroughsForUnit,
    activateWalkthroughs,
    setActivateWalkthroughs,
    sendSchedulingEmail,
    setSendSchedulingEmail: updateSchedulingEmail,
    selectedWalkthroughIds,
    handleWalkthroughToggle,
    handleSelectAllForUnit,
    handleDeselectAllForUnit,
    onSelectAllWalkthroughs,
    onDeselectAllWalkthroughs,
    isFetchingWalkthroughs,
    performBulkActivation,
    bulkActivateResponseData,
    isActivationSuccess,
    resetActivationState,
    isSubmitting,
    setShowConfirmation,
    confirmActivation,
    showConfirmation,
    error,
    clearError,
  };
};
